import React from 'react';
import PropTypes from 'prop-types';
import { styled, keyframes } from '@mui/material/styles';
import Box from '@mui/material/Box'; // Importar Box para el contenedor opcional
import Typography from '@mui/material/Typography'; // Para el mensaje opcional

// 1. Definir la animación keyframes
const wipeAnimation = keyframes`
  0% {
    background-position: 100%;
  }
`;

// 2. Crear el componente estilizado para el texto animado
// Usamos shouldForwardProp para evitar pasar props custom al DOM
const AnimatedText = styled('div', {
    shouldForwardProp: (prop) => prop !== 'fontSize' && prop !== 'loaderText',
})(({ theme, fontSize, loaderText }) => ({ // Recibe props fontSize y loaderText
    width: 'fit-content',
    fontSize: fontSize, // Usa el tamaño de fuente recibido
    fontFamily: 'system-ui, sans-serif',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: 'transparent',
    WebkitTextStroke: `1px ${theme.palette.text.primary}`, // Usa color del tema
    textStroke: `1px ${theme.palette.text.primary}`,
    background: `linear-gradient(90deg, transparent 33%, ${theme.palette.text.primary} 0 67%, transparent 0) 0/300% 100% no-repeat`,
    backgroundClip: 'text',
    animation: `${wipeAnimation} .5s linear infinite`,
    '&::before': {
        content: `"${loaderText}"`, // Usa el texto recibido
    },
}));

// 3. Definir el componente funcional SigertreLoader
const SigertreLoader = ({
    loaderText = "SIGERTRE", // Texto por defecto
    fontSize = "40px",      // Tamaño por defecto
    message,                // Mensaje opcional a mostrar debajo
    fullScreen = false,     // Prop para centrarlo en pantalla completa
    sx                       // Prop sx para estilos adicionales del contenedor
}) => {

    const loaderElement = <AnimatedText fontSize={fontSize} loaderText={loaderText} />;

    // Si se pide pantalla completa, lo envolvemos en un Box centrado
    if (fullScreen) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '80vh', // O altura configurable si se necesita
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    ...sx, // Aplicar estilos sx pasados al Box contenedor
                }}
            >
                {loaderElement}
                {message && ( // Mostrar mensaje solo si se proporciona
                    <Typography sx={{ mt: 3 }} variant="body1" color="textSecondary">
                        {message}
                    </Typography>
                )}
            </Box>
        );
    }

    // Si no es pantalla completa, devolver solo el texto animado
    // (o podrías añadir un modo con mensaje pero sin centrado completo)
    // Aplicamos sx directamente al AnimatedText si no es fullScreen
    return (
         <Box sx={sx}> {/* Contenedor simple para aplicar sx si no es fullScreen */}
             {loaderElement}
             {message && (
                 <Typography sx={{ mt: 1 }} variant="body2" color="textSecondary">
                     {message}
                 </Typography>
             )}
         </Box>
    );
};

// Definir PropTypes para documentación y validación
SigertreLoader.propTypes = {
    loaderText: PropTypes.string,
    fontSize: PropTypes.string,
    message: PropTypes.string,
    fullScreen: PropTypes.bool,
    sx: PropTypes.object,
};

export default SigertreLoader;